/**
 * Navigation Store Selectors and Custom Hooks
 * Provides optimized state access with memoization
 */

'use client'

import { useMemo } from 'react'

import type { NavigationStore } from './types'

// ============================================================================
// Selectors - Use string selectors for reliable change detection
// ============================================================================

export const selectPathString = (state: NavigationStore) => state.pathString
export const selectExpandedItemsString = (state: NavigationStore) => state.expandedItemsString
export const selectOpenAccordionsString = (state: NavigationStore) => state.openAccordionsString

// Direct state selectors
export const selectPath = (state: NavigationStore) => state.path
export const selectExpandedItems = (state: NavigationStore) => state.expandedItems
export const selectOpenAccordions = (state: NavigationStore) => state.openAccordions

// ============================================================================
// Custom Hook Factories
// These create hooks that need access to the store instance
// ============================================================================

type UseStore = <T>(selector: (state: NavigationStore) => T) => T

/**
 * Create navigation path hook
 * Uses string comparison internally for reliable change detection
 */
export const createUseNavigationPath = (useStore: UseStore) => () => {
  const pathString = useStore(selectPathString)
  return useMemo(() => (pathString ? pathString.split('/') : []), [pathString])
}

/**
 * Create expanded items hook
 * Uses string comparison internally for reliable change detection
 */
export const createUseExpandedItems = (useStore: UseStore) => () => {
  const expandedString = useStore(selectExpandedItemsString)
  return useMemo(() => (expandedString ? expandedString.split('|') : []), [expandedString])
}

/**
 * Create open accordions hook
 * Uses string comparison internally for reliable change detection
 */
export const createUseOpenAccordions = (useStore: UseStore) => () => {
  const accordionsString = useStore(selectOpenAccordionsString)
  return useMemo(() => (accordionsString ? accordionsString.split('|') : []), [accordionsString])
}
